import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Grid, Card, CardContent,
  Chip, Divider, CircularProgress, Alert, Stack, IconButton, Tooltip,
  Tabs, Tab, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TextField, InputAdornment, Badge, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TerminalIcon from '@mui/icons-material/Terminal';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import SearchIcon from '@mui/icons-material/Search';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TableChartIcon from '@mui/icons-material/TableChart';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import Swal from 'sweetalert2';
import config from '../../config/config';

export default function ControlBD() {
  const apiUrl = config.apiBaseUrl;

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [report, setReport] = useState(null);
  const [logs, setLogs] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [searchFilter, setSearchFilter] = useState('');

  // Modal Visor de Código SQL
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [codeData, setCodeData] = useState({ name: '', type: '', sql: '', env: 'test' });
  const [codeLoading, setCodeLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!apiUrl) return;
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/api/database/status`);
      if (res.data?.ok) {
        setReport(res.data.data);
        setLastUpdated(new Date().toLocaleTimeString());
      }
    } catch (err) {
      console.error('Error al cargar estado de bases de datos:', err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const ejecutarAccion = async (endpoint, title, text, confirmText, color = '#2563eb', body = {}) => {
    const result = await Swal.fire({
      title,
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: color,
      cancelButtonColor: '#64748b',
      confirmButtonText: confirmText,
      cancelButtonText: 'Cancelar',
      customClass: {
        title: 'swal2-title-clamp',
        popup: 'swal2-popup-clamp'
      }
    });

    if (!result.isConfirmed) return;

    setActionLoading(title);
    try {
      const res = await axios.post(`${apiUrl}${endpoint}`, body);
      if (res.data?.ok) {
        setLogs(res.data.logs || [res.data.message]);
        await Swal.fire('¡Operación Exitosa!', res.data.message, 'success');
        fetchStatus();
      } else {
        await Swal.fire('Error', res.data?.error || 'No se pudo completar la operación', 'error');
      }
    } catch (err) {
      await Swal.fire('Error', err.response?.data?.error || err.message, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const verCodigoSQL = async (name, type = 'procedure', env = 'test') => {
    setCodeLoading(true);
    setCodeModalOpen(true);
    setCopied(false);
    try {
      const res = await axios.get(`${apiUrl}/api/database/view-code`, {
        params: { name, type, env }
      });
      if (res.data?.ok) {
        setCodeData({ name, type, sql: res.data.sql, env });
      }
    } catch (err) {
      setCodeData({ name, type, sql: `Error al cargar código: ${err.message}`, env });
    } finally {
      setCodeLoading(false);
    }
  };

  const copiarAlPortapapeles = () => {
    if (!codeData.sql) return;
    navigator.clipboard.writeText(codeData.sql);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const envs = report?.environments || {};
  const activeDbName = report?.activeAppDatabase?.database || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'PagosEseasaTest' : 'PagosEseasa');
  const isSynced = report?.isFullySynchronized;
  const isDataSynced = report?.isDataFullySynchronized;

  const filteredTables = (report?.tablesComparison || []).filter(t => 
    t.tableName.toLowerCase().includes(searchFilter.toLowerCase())
  );

  const filteredSPs = (report?.spComparison || []).filter(s => 
    s.name.toLowerCase().includes(searchFilter.toLowerCase())
  );

  const filteredViews = (report?.viewComparison || []).filter(v => 
    v.name.toLowerCase().includes(searchFilter.toLowerCase())
  );

  const filteredCols = (report?.colComparison || []).filter(c => 
    c.column.toLowerCase().includes(searchFilter.toLowerCase())
  );

  const modifiedSPsCount = (report?.spComparison || []).filter(s => s.status !== 'SINCRONIZADO').length;
  const modifiedViewsCount = (report?.viewComparison || []).filter(v => v.status !== 'SINCRONIZADO').length;
  const dataDiffsCount = (report?.tablesComparison || []).filter(t => !t.isDataSynced).length;

  return (
    <Box sx={{
      p: 'clamp(8px, 1.5vw, 24px)',
      bgcolor: '#f8fafc',
      height: '100%',
      overflowY: 'auto',
      boxSizing: 'border-box'
    }}>
      
      {/* ── CABECERA PRINCIPAL RESPONSIVE ── */}
      <Paper
        elevation={0}
        sx={{
          p: 'clamp(12px, 1.6vw, 22px)',
          mb: 'clamp(12px, 1.6vw, 24px)',
          borderRadius: 'clamp(10px, 1.2vw, 16px)',
          border: '1px solid #e2e8f0',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: '#ffffff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 'clamp(8px, 1.2vw, 16px)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 'clamp(8px, 1.2vw, 16px)' }}>
          <Box sx={{
            p: 'clamp(8px, 1vw, 12px)',
            bgcolor: 'rgba(255,255,255,0.1)',
            borderRadius: 'clamp(8px, 1vw, 12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <StorageIcon sx={{ fontSize: 'clamp(22px, 2.2vw, 32px)', color: '#38bdf8' }} />
          </Box>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.3 }}>
              <Typography sx={{
                fontWeight: 800,
                letterSpacing: '-0.02em',
                fontSize: 'clamp(0.95rem, 0.85rem + 0.35vw, 1.25rem)',
                lineHeight: 1.2
              }}>
                Centro de Control, Integridad & Replicación de Base de Datos
              </Typography>
              <Chip
                label={`🟢 BASE ACTIVA EN USO: ${activeDbName}`}
                size="small"
                sx={{
                  bgcolor: '#10b981',
                  color: '#ffffff',
                  fontWeight: 900,
                  fontSize: 'clamp(0.62rem, 0.58rem + 0.1vw, 0.72rem)',
                  height: 'clamp(20px, 1.8vw, 24px)',
                  letterSpacing: '0.02em',
                  boxShadow: '0 0 10px rgba(16,185,129,0.5)',
                  px: 0.5
                }}
              />
            </Stack>
            <Typography sx={{ color: '#94a3b8', fontSize: 'clamp(0.68rem, 0.64rem + 0.1vw, 0.8rem)' }}>
              Parámetros cargados desde tu archivo <code>.env</code> • Host Principal: <strong>{envs.prod?.host || '172.16.112.210'}</strong>
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          {lastUpdated && (
            <Typography sx={{ color: '#94a3b8', fontSize: 'clamp(0.65rem, 0.60rem + 0.08vw, 0.75rem)' }}>
              Último escaneo: <strong>{lastUpdated}</strong>
            </Typography>
          )}
          <Button
            variant="contained"
            size="small"
            onClick={fetchStatus}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon sx={{ fontSize: 'clamp(14px, 1.2vw, 18px)' }} />}
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: '#ffffff',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.78rem)',
              px: 'clamp(10px, 1vw, 16px)',
              py: 'clamp(4px, 0.6vw, 8px)',
              minHeight: 'clamp(28px, 2.2vw, 34px)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
            }}
          >
            {loading ? 'Escaneando...' : 'Re-Escanear BD'}
          </Button>
        </Stack>
      </Paper>

      {/* ── SEMÁFOROS GLOBALES DE SALUD ── */}
      <Grid container spacing={'clamp(8px, 1.2vw, 16px)'} sx={{ mb: 'clamp(12px, 1.5vw, 24px)' }}>
        <Grid item xs={12} md={6}>
          {isSynced ? (
            <Alert
              icon={<CheckCircleIcon fontSize="inherit" />}
              severity="success"
              sx={{
                borderRadius: 'clamp(8px, 1vw, 12px)',
                border: '1px solid #bbf7d0',
                fontWeight: 600,
                fontSize: 'clamp(0.72rem, 0.68rem + 0.1vw, 0.82rem)',
                py: 'clamp(4px, 0.6vw, 8px)'
              }}
            >
              🎉 <strong>Estructura & Código 100% Sincronizados:</strong> Todos los SPs, Vistas y Columnas son idénticos.
            </Alert>
          ) : (
            <Alert
              icon={<WarningAmberIcon fontSize="inherit" />}
              severity="warning"
              sx={{
                borderRadius: 'clamp(8px, 1vw, 12px)',
                border: '1px solid #fef08a',
                fontWeight: 600,
                fontSize: 'clamp(0.72rem, 0.68rem + 0.1vw, 0.82rem)',
                py: 'clamp(4px, 0.6vw, 8px)'
              }}
            >
              ⚠️ <strong>Diferencias de Código / Esquema:</strong> Hay {modifiedSPsCount} SPs o {modifiedViewsCount} Vistas modificadas o pendientes de replicar.
            </Alert>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {isDataSynced ? (
            <Alert
              icon={<CheckCircleIcon fontSize="inherit" />}
              severity="success"
              sx={{
                borderRadius: 'clamp(8px, 1vw, 12px)',
                border: '1px solid #bbf7d0',
                fontWeight: 600,
                fontSize: 'clamp(0.72rem, 0.68rem + 0.1vw, 0.82rem)',
                py: 'clamp(4px, 0.6vw, 8px)'
              }}
            >
              📊 <strong>Datos e Información:</strong> Todas las tablas tienen exactamente la misma cantidad de registros.
            </Alert>
          ) : (
            <Alert
              icon={<WarningAmberIcon fontSize="inherit" />}
              severity="info"
              sx={{
                borderRadius: 'clamp(8px, 1vw, 12px)',
                border: '1px solid #bae6fd',
                fontWeight: 600,
                fontSize: 'clamp(0.72rem, 0.68rem + 0.1vw, 0.82rem)',
                bgcolor: '#f0f9ff',
                color: '#0369a1',
                py: 'clamp(4px, 0.6vw, 8px)'
              }}
            >
              📦 <strong>Información desactualizada en Test:</strong> Existen {dataDiffsCount} tablas con diferencia de registros respecto a Producción.
            </Alert>
          )}
        </Grid>
      </Grid>

      {/* ── PESTAÑAS DE INSPECCIÓN PROFUNDA ── */}
      <Paper elevation={0} sx={{
        borderRadius: 'clamp(10px, 1.2vw, 16px)',
        border: '1px solid #cbd5e1',
        bgcolor: '#ffffff',
        mb: 'clamp(12px, 1.5vw, 24px)'
      }}>
        <Box sx={{
          borderBottom: 1,
          borderColor: '#e2e8f0',
          px: 'clamp(10px, 1.2vw, 18px)',
          pt: 0.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Tabs
            value={currentTab}
            onChange={(e, v) => setCurrentTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 'clamp(38px, 3.2vw, 46px)',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 'clamp(0.72rem, 0.68rem + 0.1vw, 0.84rem)',
                minHeight: 'clamp(38px, 3.2vw, 46px)',
                py: 'clamp(4px, 0.6vw, 8px)',
                px: 'clamp(8px, 1vw, 14px)'
              },
              '& .Mui-selected': { color: '#0284c7' }
            }}
          >
            <Tab icon={<StorageIcon sx={{ fontSize: 'clamp(15px, 1.2vw, 18px)' }} />} iconPosition="start" label="1. Resumen & Operaciones" />
            <Tab
              icon={<Badge badgeContent={dataDiffsCount} color="warning"><TableChartIcon sx={{ fontSize: 'clamp(15px, 1.2vw, 18px)' }} /></Badge>}
              iconPosition="start"
              label="2. Comparativa de Datos (Tablas)"
            />
            <Tab
              icon={<Badge badgeContent={modifiedSPsCount} color="error"><CodeIcon sx={{ fontSize: 'clamp(15px, 1.2vw, 18px)' }} /></Badge>}
              iconPosition="start"
              label="3. Stored Procedures (SPs)"
            />
            <Tab
              icon={<Badge badgeContent={modifiedViewsCount} color="warning"><VisibilityIcon sx={{ fontSize: 'clamp(15px, 1.2vw, 18px)' }} /></Badge>}
              iconPosition="start"
              label="4. Vistas SQL"
            />
            <Tab icon={<ViewColumnIcon sx={{ fontSize: 'clamp(15px, 1.2vw, 18px)' }} />} iconPosition="start" label="5. Columnas & Tipos" />
          </Tabs>

          {currentTab > 0 && (
            <TextField
              size="small"
              placeholder="Buscar objeto..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 18px)', color: '#94a3b8' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 'clamp(160px, 18vw, 240px)',
                my: 0.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.78rem)',
                  height: 'clamp(28px, 2.2vw, 34px)'
                }
              }}
            />
          )}
        </Box>

        {/* ── TAB 0: RESUMEN Y OPERACIONES RÁPIDAS ── */}
        {currentTab === 0 && (
          <Box sx={{ p: 'clamp(12px, 1.6vw, 22px)' }}>
            <Grid container spacing={'clamp(12px, 1.5vw, 20px)'} sx={{ mb: 'clamp(14px, 1.8vw, 24px)' }}>
              
              {/* 1. PRODUCCIÓN (SERVIDOR) */}
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 'clamp(10px, 1.2vw, 14px)',
                    border: activeDbName === envs.prod?.database ? '2px solid #10b981' : '1px solid #cbd5e1',
                    borderTop: '6px solid #10b981',
                    bgcolor: activeDbName === envs.prod?.database ? '#f0fdf4' : '#ffffff',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <CardContent sx={{ p: 'clamp(10px, 1.4vw, 18px)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 900, color: '#0f172a', fontSize: 'clamp(0.78rem, 0.74rem + 0.12vw, 0.90rem)' }}>
                          🏢 PRODUCCIÓN (SERVIDOR)
                        </Typography>
                        <Box sx={{ mt: 0.5, p: 'clamp(4px, 0.6vw, 8px)', bgcolor: '#f1f5f9', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                          <Typography sx={{ display: 'block', color: '#1e293b', fontWeight: 800, fontFamily: 'monospace', fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.74rem)' }}>
                            Host: {envs.prod?.host || '172.16.112.210'}
                          </Typography>
                          <Typography sx={{ display: 'block', color: '#059669', fontWeight: 800, fontFamily: 'monospace', fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.74rem)' }}>
                            BD: {envs.prod?.database || 'PagosEseasa'}
                          </Typography>
                        </Box>
                      </Box>
                      <Stack spacing={0.5} alignItems="flex-end">
                        <Chip label="ONLINE" size="small" color="success" sx={{ fontWeight: 800, height: 'clamp(18px, 1.6vw, 22px)', fontSize: 'clamp(0.58rem, 0.54rem + 0.08vw, 0.65rem)' }} />
                        {activeDbName === envs.prod?.database && (
                          <Chip label="⭐ EN USO" size="small" sx={{ bgcolor: '#10b981', color: '#ffffff', fontWeight: 900, height: 'clamp(16px, 1.4vw, 20px)', fontSize: 'clamp(0.55rem, 0.50rem + 0.08vw, 0.60rem)' }} />
                        )}
                      </Stack>
                    </Box>
                    <Divider sx={{ my: 'clamp(6px, 1vw, 12px)' }} />
                    <Grid container spacing={1} sx={{ textAlign: 'center' }}>
                      <Grid item xs={3}>
                        <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.70rem)' }}>Tablas</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: 'clamp(0.85rem, 0.80rem + 0.2vw, 1.15rem)' }}>{envs.prod?.tablesCount ?? 47}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.70rem)' }}>SPs</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: 'clamp(0.85rem, 0.80rem + 0.2vw, 1.15rem)' }}>{envs.prod?.spsCount ?? 47}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.70rem)' }}>Vistas</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: 'clamp(0.85rem, 0.80rem + 0.2vw, 1.15rem)' }}>{envs.prod?.viewsCount ?? 10}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.70rem)' }}>Registros</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#10b981', fontSize: 'clamp(0.74rem, 0.70rem + 0.15vw, 0.90rem)', mt: 0.3 }}>
                          {(envs.prod?.totalRows ?? 0).toLocaleString()}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* 2. TEST / PRUEBAS (LOCAL) */}
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 'clamp(10px, 1.2vw, 14px)',
                    border: activeDbName === envs.test?.database ? '2px solid #f59e0b' : '1px solid #cbd5e1',
                    borderTop: '6px solid #f59e0b',
                    bgcolor: activeDbName === envs.test?.database ? '#fffbeb' : '#ffffff',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <CardContent sx={{ p: 'clamp(10px, 1.4vw, 18px)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 900, color: '#0f172a', fontSize: 'clamp(0.78rem, 0.74rem + 0.12vw, 0.90rem)' }}>
                          🧪 TEST / LOCAL (PRUEBAS)
                        </Typography>
                        <Box sx={{ mt: 0.5, p: 'clamp(4px, 0.6vw, 8px)', bgcolor: '#fef3c7', borderRadius: '6px', border: '1px solid #fde68a' }}>
                          <Typography sx={{ display: 'block', color: '#1e293b', fontWeight: 800, fontFamily: 'monospace', fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.74rem)' }}>
                            Host: {envs.test?.host || '172.16.112.210'}
                          </Typography>
                          <Typography sx={{ display: 'block', color: '#d97706', fontWeight: 800, fontFamily: 'monospace', fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.74rem)' }}>
                            BD: {envs.test?.database || 'PagosEseasaTest'}
                          </Typography>
                        </Box>
                      </Box>
                      <Stack spacing={0.5} alignItems="flex-end">
                        <Chip label="ONLINE" size="small" color="warning" sx={{ fontWeight: 800, height: 'clamp(18px, 1.6vw, 22px)', fontSize: 'clamp(0.58rem, 0.54rem + 0.08vw, 0.65rem)' }} />
                        {activeDbName === envs.test?.database && (
                          <Chip label="⭐ ACTIVA EN TU PC" size="small" sx={{ bgcolor: '#f59e0b', color: '#ffffff', fontWeight: 900, height: 'clamp(16px, 1.4vw, 20px)', fontSize: 'clamp(0.55rem, 0.50rem + 0.08vw, 0.60rem)' }} />
                        )}
                      </Stack>
                    </Box>
                    <Divider sx={{ my: 'clamp(6px, 1vw, 12px)' }} />
                    <Grid container spacing={1} sx={{ textAlign: 'center' }}>
                      <Grid item xs={3}>
                        <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.70rem)' }}>Tablas</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: 'clamp(0.85rem, 0.80rem + 0.2vw, 1.15rem)' }}>{envs.test?.tablesCount ?? 47}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.70rem)' }}>SPs</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: 'clamp(0.85rem, 0.80rem + 0.2vw, 1.15rem)' }}>{envs.test?.spsCount ?? 47}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.70rem)' }}>Vistas</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: 'clamp(0.85rem, 0.80rem + 0.2vw, 1.15rem)' }}>{envs.test?.viewsCount ?? 10}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.70rem)' }}>Registros</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#f59e0b', fontSize: 'clamp(0.74rem, 0.70rem + 0.15vw, 0.90rem)', mt: 0.3 }}>
                          {(envs.test?.totalRows ?? 0).toLocaleString()}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* 3. ESPEJO FAILOVER */}
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 'clamp(10px, 1.2vw, 14px)',
                    border: '1px solid #cbd5e1',
                    borderTop: '6px solid #0284c7',
                    bgcolor: '#ffffff',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <CardContent sx={{ p: 'clamp(10px, 1.4vw, 18px)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 900, color: '#0f172a', fontSize: 'clamp(0.78rem, 0.74rem + 0.12vw, 0.90rem)' }}>
                          🪞 ESPEJO FAILOVER (RÉPLICA)
                        </Typography>
                        <Box sx={{ mt: 0.5, p: 'clamp(4px, 0.6vw, 8px)', bgcolor: '#f0f9ff', borderRadius: '6px', border: '1px solid #e0f2fe' }}>
                          <Typography sx={{ display: 'block', color: '#1e293b', fontWeight: 800, fontFamily: 'monospace', fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.74rem)' }}>
                            Host: {envs.mirror?.host || '10.10.30.5'}
                          </Typography>
                          <Typography sx={{ display: 'block', color: '#0284c7', fontWeight: 800, fontFamily: 'monospace', fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.74rem)' }}>
                            BD: {envs.mirror?.database || 'PagosEseasa'}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        label={envs.mirror?.status === 'ONLINE' ? 'ONLINE' : 'OFFLINE'}
                        size="small"
                        color={envs.mirror?.status === 'ONLINE' ? 'info' : 'error'}
                        sx={{ fontWeight: 800, height: 'clamp(18px, 1.6vw, 22px)', fontSize: 'clamp(0.58rem, 0.54rem + 0.08vw, 0.65rem)' }}
                      />
                    </Box>
                    <Divider sx={{ my: 'clamp(6px, 1vw, 12px)' }} />
                    <Grid container spacing={1} sx={{ textAlign: 'center' }}>
                      <Grid item xs={3}>
                        <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.70rem)' }}>Tablas</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: 'clamp(0.85rem, 0.80rem + 0.2vw, 1.15rem)' }}>{envs.mirror?.tablesCount ?? 47}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.70rem)' }}>SPs</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: 'clamp(0.85rem, 0.80rem + 0.2vw, 1.15rem)' }}>{envs.mirror?.spsCount ?? 47}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.70rem)' }}>Vistas</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: 'clamp(0.85rem, 0.80rem + 0.2vw, 1.15rem)' }}>{envs.mirror?.viewsCount ?? 10}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.70rem)' }}>Registros</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#0284c7', fontSize: 'clamp(0.74rem, 0.70rem + 0.15vw, 0.90rem)', mt: 0.3 }}>
                          {(envs.mirror?.totalRows ?? 0).toLocaleString()}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* BOTONES DE OPERACIÓN FLUIDOS */}
            <Typography sx={{ fontWeight: 800, color: '#0f172a', mb: 'clamp(8px, 1.2vw, 16px)', fontSize: 'clamp(0.78rem, 0.74rem + 0.1vw, 0.90rem)' }}>
              ⚡ Operaciones Globales
            </Typography>
            <Grid container spacing={'clamp(8px, 1.2vw, 16px)'}>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={!!actionLoading}
                  onClick={() => ejecutarAccion(
                    '/api/database/clone-data-to-test',
                    '¿Clonar Datos de Producción a Test?',
                    'Esta acción reemplazará los datos de la base de pruebas con la información real de Producción.',
                    'Sí, Clonar Datos a Test',
                    '#f59e0b'
                  )}
                  startIcon={<CloudDownloadIcon sx={{ fontSize: 'clamp(16px, 1.3vw, 20px)' }} />}
                  sx={{
                    bgcolor: '#f59e0b',
                    color: '#ffffff',
                    p: 'clamp(8px, 1vw, 14px)',
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 'clamp(0.70rem, 0.66rem + 0.1vw, 0.82rem)',
                    '&:hover': { bgcolor: '#d97706' }
                  }}
                >
                  📦 Clonar Datos Prod ➡️ Test
                </Button>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={!!actionLoading}
                  onClick={() => ejecutarAccion(
                    '/api/database/clone-data-to-mirror',
                    '¿Clonar Datos de Producción al Espejo?',
                    'Esta acción volcará toda la información de Producción al servidor espejo (10.10.30.5).',
                    'Sí, Clonar Datos al Espejo',
                    '#0284c7'
                  )}
                  startIcon={<SyncAltIcon sx={{ fontSize: 'clamp(16px, 1.3vw, 20px)' }} />}
                  sx={{
                    bgcolor: '#0284c7',
                    color: '#ffffff',
                    p: 'clamp(8px, 1vw, 14px)',
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 'clamp(0.70rem, 0.66rem + 0.1vw, 0.82rem)',
                    '&:hover': { bgcolor: '#0369a1' }
                  }}
                >
                  🪞 Clonar Datos Prod ➡️ Espejo
                </Button>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={!!actionLoading}
                  onClick={() => ejecutarAccion(
                    '/api/database/sync-schema-to-prod',
                    '¿Desplegar Esquema de Test a Producción y Espejo?',
                    'Se sincronizarán todas las nuevas columnas, Vistas y Stored Procedures probados en Test hacia la base de Producción y el Espejo.',
                    'Sí, Desplegar a Producción',
                    '#10b981'
                  )}
                  startIcon={<CloudUploadIcon sx={{ fontSize: 'clamp(16px, 1.3vw, 20px)' }} />}
                  sx={{
                    bgcolor: '#10b981',
                    color: '#ffffff',
                    p: 'clamp(8px, 1vw, 14px)',
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 'clamp(0.70rem, 0.66rem + 0.1vw, 0.82rem)',
                    '&:hover': { bgcolor: '#059669' }
                  }}
                >
                  🚀 Desplegar Esquema Test ➡️ Prod
                </Button>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={!!actionLoading}
                  onClick={() => ejecutarAccion(
                    '/api/database/sync-schema-to-test',
                    '¿Traer Esquema de Producción a Test?',
                    'Se sincronizarán los Stored Procedures, Vistas y Columnas de Producción hacia Test y el Espejo.',
                    'Sí, Traer Esquema',
                    '#64748b'
                  )}
                  startIcon={<CloudSyncIcon sx={{ fontSize: 'clamp(16px, 1.3vw, 20px)' }} />}
                  sx={{
                    color: '#475569',
                    borderColor: '#cbd5e1',
                    p: 'clamp(8px, 1vw, 14px)',
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 'clamp(0.70rem, 0.66rem + 0.1vw, 0.82rem)',
                    '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' }
                  }}
                >
                  📥 Traer Esquema Prod ➡️ Test
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ── TAB 1: COMPARATIVA DE DATOS / TABLAS ── */}
        {currentTab === 1 && (
          <Box sx={{ p: 'clamp(10px, 1.4vw, 18px)' }}>
            <TableContainer sx={{ maxHeight: 'clamp(320px, 50vh, 560px)' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>Tabla</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>🏢 Filas en Prod</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>🧪 Filas en Test</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>🪞 Filas en Espejo</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>Diferencia Test vs Prod</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>Acción Individual</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTables.map((row) => (
                    <TableRow key={row.tableName} hover sx={{ bgcolor: !row.isDataSynced ? '#fffbeb' : 'inherit' }}>
                      <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.80rem)' }}>
                        {row.tableName}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#10b981', fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.80rem)' }}>
                        {row.prodCount !== null ? row.prodCount.toLocaleString() : 'N/D'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#f59e0b', fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.80rem)' }}>
                        {row.testCount !== null ? row.testCount.toLocaleString() : 'N/D'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#0284c7', fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.80rem)' }}>
                        {row.mirrorCount !== null ? row.mirrorCount.toLocaleString() : 'N/D'}
                      </TableCell>
                      <TableCell align="center">
                        {row.diffTestVsProd === 0 ? (
                          <Chip label="Coincide (0 dif)" size="small" color="success" sx={{ height: 'clamp(18px, 1.5vw, 22px)', fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.68rem)', fontWeight: 700 }} />
                        ) : (
                          <Chip
                            label={`${row.diffTestVsProd > 0 ? '+' : ''}${row.diffTestVsProd} filas`}
                            size="small"
                            color={row.diffTestVsProd < 0 ? 'error' : 'warning'}
                            sx={{ height: 'clamp(18px, 1.5vw, 22px)', fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.68rem)', fontWeight: 800 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!!actionLoading}
                          onClick={() => ejecutarAccion(
                            '/api/database/clone-single-table',
                            `¿Clonar tabla ${row.tableName} a Test?`,
                            `Esta acción copiará exactamente los registros de ${row.tableName} desde Producción a Test.`,
                            'Clonar Tabla',
                            '#f59e0b',
                            { tableName: row.tableName, target: 'test' }
                          )}
                          sx={{
                            textTransform: 'none',
                            py: 'clamp(2px, 0.4vw, 4px)',
                            px: 'clamp(6px, 0.8vw, 12px)',
                            fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.72rem)',
                            borderRadius: '6px'
                          }}
                        >
                          Copiar a Test
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* ── TAB 2: STORED PROCEDURES (SPs) ── */}
        {currentTab === 2 && (
          <Box sx={{ p: 'clamp(10px, 1.4vw, 18px)' }}>
            <TableContainer sx={{ maxHeight: 'clamp(320px, 50vh, 560px)' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>Nombre del SP</TableCell>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>Estado de Código</TableCell>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>Diagnóstico Interno</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>Acciones Rápidas</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSPs.map((sp) => {
                    const isMod = sp.status === 'MODIFICADO' || sp.status === 'SOLO_EN_TEST';
                    const isMirrorDiff = sp.status === 'DESFASADO_ESPEJO';

                    return (
                      <TableRow key={sp.name} hover sx={{ bgcolor: isMod ? '#fef2f2' : (isMirrorDiff ? '#f0f9ff' : 'inherit') }}>
                        <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.80rem)' }}>
                          {sp.name}
                        </TableCell>
                        <TableCell>
                          {sp.status === 'SINCRONIZADO' && (
                            <Chip label="100% Idéntico" size="small" color="success" sx={{ height: 'clamp(18px, 1.5vw, 22px)', fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.68rem)', fontWeight: 700 }} />
                          )}
                          {sp.status === 'MODIFICADO' && (
                            <Chip label="⚠️ Editado en Test" size="small" color="error" sx={{ height: 'clamp(18px, 1.5vw, 22px)', fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.68rem)', fontWeight: 800 }} />
                          )}
                          {sp.status === 'SOLO_EN_TEST' && (
                            <Chip label="✨ Nuevo en Test" size="small" color="warning" sx={{ height: 'clamp(18px, 1.5vw, 22px)', fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.68rem)', fontWeight: 800 }} />
                          )}
                          {sp.status === 'SOLO_EN_PROD' && (
                            <Chip label="Falta en Test" size="small" color="info" sx={{ height: 'clamp(18px, 1.5vw, 22px)', fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.68rem)', fontWeight: 800 }} />
                          )}
                          {sp.status === 'DESFASADO_ESPEJO' && (
                            <Chip label="Desfasado en Espejo" size="small" color="info" sx={{ height: 'clamp(18px, 1.5vw, 22px)', fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.68rem)', fontWeight: 800 }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.78rem)', color: isMod ? '#dc2626' : '#64748b' }}>
                          {sp.detail}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Ver o copiar el código SQL del procedimiento">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => verCodigoSQL(sp.name, 'procedure', 'test')}
                                startIcon={<CodeIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 16px)' }} />}
                                sx={{
                                  textTransform: 'none',
                                  py: 'clamp(2px, 0.4vw, 4px)',
                                  px: 'clamp(6px, 0.8vw, 12px)',
                                  fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.72rem)',
                                  borderRadius: '6px'
                                }}
                              >
                                Ver / Copiar SQL
                              </Button>
                            </Tooltip>

                            {isMod && (
                              <Tooltip title="Desplegar solo este SP hacia Producción y Espejo">
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={!!actionLoading}
                                  onClick={() => ejecutarAccion(
                                    '/api/database/sync-single-object',
                                    `¿Desplegar SP ${sp.name} a Producción?`,
                                    `Se reemplazará el Stored Procedure ${sp.name} en Producción y en el Espejo con el código probado en Test.`,
                                    'Desplegar este SP',
                                    '#10b981',
                                    { type: 'procedure', name: sp.name, direction: 'test-to-prod' }
                                  )}
                                  startIcon={<CloudUploadIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 16px)' }} />}
                                  sx={{
                                    bgcolor: '#10b981',
                                    color: '#ffffff',
                                    textTransform: 'none',
                                    py: 'clamp(2px, 0.4vw, 4px)',
                                    px: 'clamp(6px, 0.8vw, 12px)',
                                    fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.72rem)',
                                    borderRadius: '6px',
                                    '&:hover': { bgcolor: '#059669' }
                                  }}
                                >
                                  Desplegar a Prod
                                </Button>
                              </Tooltip>
                            )}

                            {isMirrorDiff && (
                              <Tooltip title="Sincronizar este SP hacia el Servidor Espejo">
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={!!actionLoading}
                                  onClick={() => ejecutarAccion(
                                    '/api/database/sync-single-object',
                                    `¿Sincronizar SP ${sp.name} al Espejo?`,
                                    `Se copiará la versión de Producción al Servidor Espejo (10.10.30.5).`,
                                    'Sincronizar a Espejo',
                                    '#0284c7',
                                    { type: 'procedure', name: sp.name, direction: 'prod-to-test' }
                                  )}
                                  startIcon={<SyncAltIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 16px)' }} />}
                                  sx={{
                                    bgcolor: '#0284c7',
                                    color: '#ffffff',
                                    textTransform: 'none',
                                    py: 'clamp(2px, 0.4vw, 4px)',
                                    px: 'clamp(6px, 0.8vw, 12px)',
                                    fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.72rem)',
                                    borderRadius: '6px',
                                    '&:hover': { bgcolor: '#0369a1' }
                                  }}
                                >
                                  Sincronizar a Espejo
                                </Button>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* ── TAB 3: VISTAS SQL ── */}
        {currentTab === 3 && (
          <Box sx={{ p: 'clamp(10px, 1.4vw, 18px)' }}>
            <TableContainer sx={{ maxHeight: 'clamp(320px, 50vh, 560px)' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>Nombre de la Vista</TableCell>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>Detalle</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>Acciones Rápidas</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredViews.map((v) => {
                    const isMod = v.status === 'MODIFICADO' || v.status === 'SOLO_EN_TEST';
                    const isMirrorDiff = v.status === 'DESFASADO_ESPEJO';

                    return (
                      <TableRow key={v.name} hover sx={{ bgcolor: isMod ? '#fef2f2' : (isMirrorDiff ? '#f0f9ff' : 'inherit') }}>
                        <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.80rem)' }}>
                          {v.name}
                        </TableCell>
                        <TableCell>
                          {v.status === 'SINCRONIZADO' && (
                            <Chip label="100% Idéntica" size="small" color="success" sx={{ height: 'clamp(18px, 1.5vw, 22px)', fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.68rem)', fontWeight: 700 }} />
                          )}
                          {v.status === 'MODIFICADO' && (
                            <Chip label="⚠️ Modificada en Test" size="small" color="error" sx={{ height: 'clamp(18px, 1.5vw, 22px)', fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.68rem)', fontWeight: 800 }} />
                          )}
                          {v.status === 'SOLO_EN_TEST' && (
                            <Chip label="✨ Nueva en Test" size="small" color="warning" sx={{ height: 'clamp(18px, 1.5vw, 22px)', fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.68rem)', fontWeight: 800 }} />
                          )}
                          {v.status === 'DESFASADO_ESPEJO' && (
                            <Chip label="Desfasada en Espejo" size="small" color="info" sx={{ height: 'clamp(18px, 1.5vw, 22px)', fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.68rem)', fontWeight: 800 }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.78rem)', color: isMod ? '#dc2626' : '#64748b' }}>
                          {v.detail}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => verCodigoSQL(v.name, 'view', 'test')}
                              startIcon={<CodeIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 16px)' }} />}
                              sx={{
                                textTransform: 'none',
                                py: 'clamp(2px, 0.4vw, 4px)',
                                px: 'clamp(6px, 0.8vw, 12px)',
                                fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.72rem)',
                                borderRadius: '6px'
                              }}
                            >
                              Ver / Copiar SQL
                            </Button>

                            {isMod && (
                              <Button
                                size="small"
                                variant="contained"
                                disabled={!!actionLoading}
                                onClick={() => ejecutarAccion(
                                  '/api/database/sync-single-object',
                                  `¿Desplegar Vista ${v.name} a Producción?`,
                                  `Se actualizará la vista ${v.name} en Producción y en el Espejo con el código de Test.`,
                                  'Desplegar Vista',
                                  '#10b981',
                                  { type: 'view', name: v.name, direction: 'test-to-prod' }
                                )}
                                startIcon={<CloudUploadIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 16px)' }} />}
                                sx={{
                                  bgcolor: '#10b981',
                                  color: '#ffffff',
                                  textTransform: 'none',
                                  py: 'clamp(2px, 0.4vw, 4px)',
                                  px: 'clamp(6px, 0.8vw, 12px)',
                                  fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.72rem)',
                                  borderRadius: '6px',
                                  '&:hover': { bgcolor: '#059669' }
                                }}
                              >
                                Desplegar a Prod
                              </Button>
                            )}

                            {isMirrorDiff && (
                              <Button
                                size="small"
                                variant="contained"
                                disabled={!!actionLoading}
                                onClick={() => ejecutarAccion(
                                  '/api/database/sync-single-object',
                                  `¿Sincronizar Vista ${v.name} al Espejo?`,
                                  `Se copiará la vista desde Producción al Servidor Espejo (10.10.30.5).`,
                                  'Sincronizar a Espejo',
                                  '#0284c7',
                                  { type: 'view', name: v.name, direction: 'prod-to-test' }
                                )}
                                startIcon={<SyncAltIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 16px)' }} />}
                                sx={{
                                  bgcolor: '#0284c7',
                                  color: '#ffffff',
                                  textTransform: 'none',
                                  py: 'clamp(2px, 0.4vw, 4px)',
                                  px: 'clamp(6px, 0.8vw, 12px)',
                                  fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.72rem)',
                                  borderRadius: '6px',
                                  '&:hover': { bgcolor: '#0369a1' }
                                }}
                              >
                                Sincronizar a Espejo
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* ── TAB 4: COLUMNAS & TIPOS ── */}
        {currentTab === 4 && (
          <Box sx={{ p: 'clamp(10px, 1.4vw, 18px)' }}>
            {filteredCols.length === 0 ? (
              <Alert severity="success" sx={{ borderRadius: '10px', fontSize: 'clamp(0.72rem, 0.68rem + 0.1vw, 0.82rem)' }}>
                ✅ Todas las columnas y tipos de datos coinciden perfectamente entre Producción y Test.
              </Alert>
            ) : (
              <TableContainer sx={{ maxHeight: 'clamp(320px, 50vh, 560px)' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>Tabla.Columna</TableCell>
                      <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>Estado</TableCell>
                      <TableCell sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>Detalle</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, bgcolor: '#f8fafc', fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.82rem)' }}>Acción Rápida</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCols.map((c, idx) => {
                      const isTestToProd = c.status === 'NUEVA_EN_TEST' || c.status === 'TIPO_MODIFICADO';

                      return (
                        <TableRow key={idx} hover sx={{ bgcolor: '#fffbeb' }}>
                          <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.80rem)' }}>
                            {c.column}
                          </TableCell>
                          <TableCell>
                            <Chip label={c.status} size="small" color={isTestToProd ? 'warning' : 'info'} sx={{ height: 'clamp(18px, 1.5vw, 22px)', fontSize: 'clamp(0.60rem, 0.56rem + 0.08vw, 0.68rem)', fontWeight: 800 }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.78rem)', color: '#64748b' }}>
                            {c.detail}
                          </TableCell>
                          <TableCell align="right">
                            {isTestToProd ? (
                              <Button
                                size="small"
                                variant="contained"
                                disabled={!!actionLoading}
                                onClick={() => ejecutarAccion(
                                  '/api/database/sync-single-column',
                                  `¿Aplicar columna ${c.column} a Producción?`,
                                  `Se ejecutará ALTER TABLE en Producción y en el Espejo para sincronizar la columna ${c.column}.`,
                                  'Aplicar a Producción',
                                  '#10b981',
                                  { tableName: c.tableName, columnName: c.columnName, direction: 'test-to-prod' }
                                )}
                                startIcon={<CloudUploadIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 16px)' }} />}
                                sx={{
                                  bgcolor: '#10b981',
                                  color: '#ffffff',
                                  textTransform: 'none',
                                  py: 'clamp(2px, 0.4vw, 4px)',
                                  px: 'clamp(6px, 0.8vw, 12px)',
                                  fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.72rem)',
                                  borderRadius: '6px',
                                  '&:hover': { bgcolor: '#059669' }
                                }}
                              >
                                Aplicar a Prod
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={!!actionLoading}
                                onClick={() => ejecutarAccion(
                                  '/api/database/sync-single-column',
                                  `¿Traer columna ${c.column} a Test?`,
                                  `Se agregará la columna ${c.column} a la base de datos de pruebas (Test).`,
                                  'Traer a Test',
                                  '#0284c7',
                                  { tableName: c.tableName, columnName: c.columnName, direction: 'prod-to-test' }
                                )}
                                startIcon={<CloudDownloadIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 16px)' }} />}
                                sx={{
                                  textTransform: 'none',
                                  py: 'clamp(2px, 0.4vw, 4px)',
                                  px: 'clamp(6px, 0.8vw, 12px)',
                                  fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.72rem)',
                                  borderRadius: '6px'
                                }}
                              >
                                Traer a Test
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

      </Paper>

      {/* ── MODAL VISOR DE CÓDIGO SQL RESPONSIVE ── */}
      <Dialog
        open={codeModalOpen}
        onClose={() => setCodeModalOpen(false)}
        maxWidth="md"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#0f172a',
          color: '#ffffff',
          py: 'clamp(8px, 1.2vw, 14px)',
          px: 'clamp(12px, 1.6vw, 20px)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon sx={{ color: '#38bdf8', fontSize: 'clamp(18px, 1.4vw, 24px)' }} />
            <Typography sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 'clamp(0.78rem, 0.74rem + 0.12vw, 0.95rem)' }}>
              {codeData.name} ({codeData.type === 'procedure' ? 'Stored Procedure' : 'Vista'})
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setCodeModalOpen(false)} sx={{ color: '#94a3b8', '&:hover': { color: '#ffffff' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 'clamp(10px, 1.4vw, 18px)', bgcolor: '#0b1120' }}>
          {codeLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: '#38bdf8' }} />
            </Box>
          ) : (
            <Box
              component="pre"
              sx={{
                p: 'clamp(10px, 1.2vw, 16px)',
                m: 0,
                borderRadius: '8px',
                bgcolor: '#020617',
                color: '#38bdf8',
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.80rem)',
                lineHeight: 1.5,
                overflowX: 'auto',
                maxHeight: 'clamp(280px, 45vh, 480px)',
                border: '1px solid #1e293b'
              }}
            >
              {codeData.sql}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{
          p: 'clamp(8px, 1.2vw, 14px)',
          bgcolor: '#0f172a',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant={codeData.env === 'test' ? 'contained' : 'outlined'}
              onClick={() => verCodigoSQL(codeData.name, codeData.type, 'test')}
              sx={{ textTransform: 'none', fontSize: 'clamp(0.65rem, 0.60rem + 0.08vw, 0.75rem)', borderRadius: '6px' }}
            >
              Ver versión en Test
            </Button>
            <Button
              size="small"
              variant={codeData.env === 'prod' ? 'contained' : 'outlined'}
              onClick={() => verCodigoSQL(codeData.name, codeData.type, 'prod')}
              sx={{ textTransform: 'none', fontSize: 'clamp(0.65rem, 0.60rem + 0.08vw, 0.75rem)', borderRadius: '6px' }}
            >
              Ver versión en Prod
            </Button>
          </Stack>

          <Button
            variant="contained"
            onClick={copiarAlPortapapeles}
            startIcon={copied ? <CheckCircleIcon /> : <ContentCopyIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 18px)' }} />}
            sx={{
              bgcolor: copied ? '#10b981' : '#0284c7',
              color: '#ffffff',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 'clamp(0.70rem, 0.66rem + 0.08vw, 0.80rem)',
              borderRadius: '8px',
              px: 'clamp(10px, 1.2vw, 16px)',
              py: 'clamp(4px, 0.6vw, 8px)',
              '&:hover': { bgcolor: copied ? '#059669' : '#0369a1' }
            }}
          >
            {copied ? '¡Copiado al Portapapeles!' : 'Copiar SQL'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── CONSOLA DE LOGS EN VIVO ── */}
      {logs.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 'clamp(10px, 1.4vw, 18px)',
            borderRadius: 'clamp(10px, 1.2vw, 14px)',
            bgcolor: '#0f172a',
            color: '#38bdf8',
            fontFamily: 'monospace',
            fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.78rem)',
            border: '1px solid #334155',
            maxHeight: 'clamp(180px, 30vh, 280px)',
            overflowY: 'auto'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#94a3b8' }}>
            <TerminalIcon sx={{ fontSize: 'clamp(15px, 1.2vw, 18px)' }} />
            <Typography sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.78rem)' }}>
              LOGS DE OPERACIÓN
            </Typography>
          </Box>
          <Divider sx={{ borderColor: '#334155', mb: 1 }} />
          {logs.map((l, idx) => (
            <Box key={idx} sx={{ py: 0.2, color: l.includes('❌') ? '#f87171' : (l.includes('✅') ? '#4ade80' : '#e2e8f0') }}>
              {l}
            </Box>
          ))}
        </Paper>
      )}

    </Box>
  );
}
